import { cloneDeep, isEqual } from "lodash";
import {
  recordHistorySnapshot,
  setWheelSegments,
  undo,
  redo,
  setHistorySuppressed,
} from "../reducers/segmentBuilder";
import { getPresentState } from "../selectors/segmentBuilderSelectors";
import { hasNoCraftContent } from "../../utils/craftHistory";

/**
 * HISTORY_SUPPRESS_MS — the single source of truth for the undo/redo re-hydration-echo settle
 * window (WEB-037/WEB-038): raised before an undo/redo dispatch, lowered this many ms later, so
 * every reactive echo the click's own re-render triggers (canvas re-serialize, debounced at
 * CANVAS_SYNC_DEBOUNCE_MS=250 in pages/SegmentBuilder/index.jsx, D3 chart feedback,
 * segment-settings initializers) has landed and been suppressed before the window closes. Every
 * undo/redo entry point (the toolbar buttons in UndoRedoControls.jsx, the page-level
 * runWithHistorySuppressed used by the AI Copilot pane) must share this ONE constant — two
 * different values here is exactly how one entry point can end its suppression window before an
 * echo from a slower entry point's debounce has fired (builder cell 16.1, gauntlet-builder2
 * 2026-08-06).
 */
export const HISTORY_SUPPRESS_MS = 600;

/**
 * withHistory — the ONE centralized wrapper for any undoable wheel/segment/block mutation.
 *
 * Wrap the mutating action (a plain action OR a thunk) so the builder records a correct, single
 * undo step for it:
 *
 *   dispatch(withHistory(setGlobalCornerRounding(20)));
 *   dispatch(withHistory(setWheelSegments(nextSegments)));
 *
 * It fixes the two root causes the assessments identified:
 *   1. Ordering — it deep-clones the PRE-mutation "present" first, applies the mutation, then
 *      records that pre-state as the restore point. This guarantees snapshot-before-mutate even
 *      though the mutation reducers write the present in place, so the FIRST undo lands on the exact
 *      prior state (the old scattered `dispatch(mutation); dispatch(pushToHistoryAction())` order
 *      destroyed the pre-change state and made the first undo a no-op).
 *   2. Duplicate / no-op entries — it records a checkpoint only when the mutation actually changed
 *      the present (isEqual guard), so no-op edits (or mutations that only touch OTHER slices, e.g.
 *      wallpaper) don't pollute the undo stack.
 *
 * The deep clone also detaches every history entry from Immer's draft graph, removing the
 * shallow-copy fragility (Bug B) where adjacent snapshots shared nested references.
 *
 * WEB-038 — undo/redo re-hydration suppression. Undoing/redoing re-hydrates the open segment (and
 * re-renders the wheel + its settings panels). That re-render fires several REACTIVE effects that
 * each dispatch a mutation through `withHistory` as a side effect of the state having changed — the
 * craft.js canvas re-serializing itself, the D3 wheel feeding its recomputed chart data back, and
 * segment-settings initializers (e.g. `handlePatternColorInitialChange`) re-deriving a value. None
 * of those are user edits; they are echoes of the undo/redo. If any of them records a history step
 * it trims the redo-future and silently cancels the user's ability to redo (WEB-037 for the canvas
 * echo; WEB-038 for the settings echo across a segment-creation boundary). Because EVERY history
 * record funnels through here, one guard at this chokepoint covers all of them: while the builder
 * has flagged an undo/redo as still settling (`historySuppressed`), we still APPLY the mutation
 * (so Redux stays in step with the re-hydrated canvas) but never record a checkpoint. The flag is
 * raised by the undo/redo handlers and lowered a short settle window later (see SegmentBuilder).
 *
 * Scope note: this covers the `segmentBuilder` history slice only. Wallpaper (wheelWallpaper),
 * color palettes (colorPalette) and carry-forward block defaults (codeblockStyles) live in
 * separate slices and are intentionally NOT folded into history in this PR.
 *
 * @param {import("@reduxjs/toolkit").AnyAction | Function} action
 */
export const withHistory = (action) => (dispatch, getState) => {
  if (getState().segmentBuilder?.historySuppressed) {
    // An undo/redo is still settling — this mutation is a re-hydration echo, not a user edit.
    // Apply it (keep Redux in step with the re-hydrated view) but never record a checkpoint.
    dispatch(action);
    return;
  }

  const before = cloneDeep(getPresentState(getState()));

  dispatch(action);

  const after = getPresentState(getState());
  if (!isEqual(before, after)) {
    dispatch(recordHistorySnapshot(before));
  }
};

/**
 * undoWithSuppression / redoWithSuppression — the ONE centralized entry point for every real
 * undo/redo trigger in the app (toolbar buttons, the AutoDeriveUndoToast "Undo" link, the AI
 * Copilot pane's undo affordance, and anywhere future). Every one of them MUST go through here
 * rather than dispatching the raw `undo()`/`redo()` action creators directly.
 *
 * Bug 16.1 (gauntlet-builder2, 2026-08-06) was two separate defects layered on each other, both
 * fixed by centralizing here:
 *   1. `UndoRedoControls.jsx`'s toolbar buttons dispatched `undo()`/`redo()` RAW — never raising
 *      `historySuppressed` — so the FIRST reactive echo the click's own re-render triggered
 *      recorded a phantom checkpoint through `withHistory` and silently cancelled the undo.
 *   2. An independent adversarial review of that fix (same day) found a SECOND raw call site
 *      (`AutoDeriveUndoToast.jsx`'s toast "Undo" button) that the first pass's commit message
 *      wrongly claimed was "the only real undo/redo entry point" — and a narrower defect in the
 *      fix itself: `UndoRedoControls.jsx` and `pages/SegmentBuilder/index.jsx` each owned an
 *      INDEPENDENT settle-timer for the SAME shared `historySuppressed` boolean, so whichever
 *      timer fired first lowered the guard for both — a classic non-reentrant-guard race.
 *
 * A single module-level timer (not one per call site) is the fix for the race: there is exactly
 * ONE owner of the settle window regardless of which UI trigger raised it, so two triggers firing
 * close together extend/replace the SAME window rather than each unaware of the other's clock.
 */
let suppressSettleTimer = null;

const dispatchSuppressed = (actionCreator) => (dispatch) => {
  if (suppressSettleTimer) clearTimeout(suppressSettleTimer);
  dispatch(setHistorySuppressed(true));
  dispatch(actionCreator());
  suppressSettleTimer = setTimeout(() => {
    suppressSettleTimer = null;
    dispatch(setHistorySuppressed(false));
  }, HISTORY_SUPPRESS_MS);
};

export const undoWithSuppression = () => dispatchSuppressed(undo);
export const redoWithSuppression = () => dispatchSuppressed(redo);

/**
 * withHistoryCommitThenSettle — commit a user action as EXACTLY ONE undo step, then open the
 * SAME re-hydration-echo suppression window undo/redo already use (see `dispatchSuppressed`
 * above), so the reactive echoes the action's own re-render fires — the craft.js canvas
 * re-serializing the freshly-opened segment, the D3 wheel feeding recomputed chart data back
 * through `handleSetChartData`, segment-settings initializers — are APPLIED (Redux stays in
 * step with the re-rendered view) but never recorded as a SECOND phantom checkpoint.
 *
 * Root cause it fixes (QA-1041, REGRESSION): "Add segment" dispatched
 * `withHistory(setWheelSegments(...))` — a correct single checkpoint — but selecting/opening
 * the new segment fired a follow-up echo that recorded a DUPLICATE checkpoint through the
 * history layer. The undo stack became `[1-seg, 2-seg, 2-seg']`, so the FIRST undo landed on
 * the duplicate 2-segment state (arc-fill + pills unchanged — no visible revert) and only a
 * SECOND undo removed the segment; redo was likewise a no-op between the two identical states.
 * Reproduced + MEASURED live on web-stg bundle main.a3cc5ef8.js, wheel 3169 (2026-08-24):
 * add -> pills=["Segment 2"]; undo#1 -> STILL ["Segment 2"] (redo re-enabled); undo#2 -> [].
 *
 * This is the SAME mechanism and the SAME chokepoint fix as the undo/redo suppression
 * (WEB-037 canvas echo, WEB-038 settings echo) — the add simply never got the settle window
 * they already had. The action's own checkpoint is recorded FIRST (via `withHistory`, so the
 * add still advances `currentIndex` and arms autosave), then the window opens for the echoes.
 * Uses the ONE shared module-level `suppressSettleTimer`, so an add immediately followed by an
 * undo/redo can't leave two timers racing to lower the same flag (bug 16.1's lesson).
 *
 * @param {import("@reduxjs/toolkit").AnyAction | Function} action
 */
export const withHistoryCommitThenSettle = (action) => (dispatch) => {
  dispatch(withHistory(action));
  if (suppressSettleTimer) clearTimeout(suppressSettleTimer);
  dispatch(setHistorySuppressed(true));
  suppressSettleTimer = setTimeout(() => {
    suppressSettleTimer = null;
    dispatch(setHistorySuppressed(false));
  }, HISTORY_SUPPRESS_MS);
};

/**
 * Parse a craft.js serialized-nodes value into a plain object for structural comparison.
 * A segment's `nodes` is a JSON string; two strings that encode the SAME tree but differ only in
 * key order / whitespace (which a craft.js serialize round-trip routinely produces) must compare
 * equal. Falls back to the raw value if it isn't parseable.
 */
const parseNodes = (nodes) => {
  if (typeof nodes !== "string") return nodes;
  try {
    return JSON.parse(nodes);
  } catch (e) {
    return nodes;
  }
};

/**
 * #builder-fresh-unsaved (round 2) — opening a never-touched EMPTY segment lazily
 * materializes a canonical empty craft.js ROOT (the keyed <Frame>'s freshly-built
 * EmptyContainerWidget template — same shape as `constants/wheelData.js`'s
 * `initialSegmentNode` — see SegmentView's `isEmptyNodeMap` load guard) purely as a
 * side effect of mounting the editor. That freshly-serialized ROOT-with-no-children
 * JSON string never byte-matches whatever "no content yet" looks like in the SAVED
 * payload (`null` on prod, or the rootless `"{}"` some records carry) even though
 * neither side has any real content — so the plain parse+isEqual compare below saw a
 * genuine diff and dirtied the wheel on a bare segment OPEN, no edit required.
 * `hasNoCraftContent` recognizes BOTH shapes of "no content" (missing/rootless/empty
 * map, and a ROOT with zero children) as equivalent, so any two contentless node maps
 * compare equal regardless of their exact serialized form.
 */
const nodesStructurallyEqual = (a, b) =>
  a === b ||
  (hasNoCraftContent(a) && hasNoCraftContent(b)) ||
  isEqual(parseNodes(a), parseNodes(b));

/**
 * Transient editor-only segment fields that are recomputed client-side on every load / segment
 * open / canvas re-serialize and are NOT part of the wheel's persisted content. A change in any of
 * these is pure editor bookkeeping, never a user edit, so the dirty/undo comparator must ignore
 * them — otherwise the mount-time reactive echoes flip one and falsely mark the wheel dirty.
 *
 *   selected     — which segment is currently open (WL-072 / EW-013: a segment click toggling this
 *                  already caused spurious "unsaved changes" + autosave churn; stripped from the
 *                  save payload there, ignored here for the same reason at the change-detector).
 *   initialNode  — a hydration scratch field (the loaded segment's pre-edit nodes snapshot); it is
 *                  reset after each save, which would otherwise read as a diff.
 *   is_modified  — a DERIVED "this segment's content changed" flag. On builder mount the craft
 *                  canvas re-serializes the open segment and the editorState effect recomputes it
 *                  as `serializedString !== parsedInitialNodeObject` → ALWAYS true → it flips
 *                  false→true with no real edit. The underlying content change (if any) is already
 *                  detected via `nodes`/settings, so the flag itself must not count as a change.
 *   _labelDisplay — a D3-render-only annotation. `createPie.js`'s `resolveLabelOverlaps` writes
 *                  `full`/`dot`/`none` directly onto each pie datum (the SAME object reference as
 *                  the segment in `hydratedChartData`, which is the Redux segment by reference when
 *                  the fill/computed-label flags are off) as a side effect of measuring rendered
 *                  label pills for overlap — purely a function of viewport size, not user content.
 *                  `arcSelected`'s click handler (index.jsx `handleSetChartData`) folds
 *                  `this.chartData` — already carrying that annotation — into the segments it
 *                  dispatches on every arc click, including a bare select. Left uncompared, a
 *                  select whose render pass just relabeled a pill looked like a genuine content
 *                  change here, recorded a checkpoint, and falsely dirtied the wheel
 *                  (#builder-fresh-unsaved round 4).
 *   isPinned      — stgfix-1008-NEW-EditWheel-1b: the SAME leak as `_labelDisplay` above, for a
 *                  different createPie.js annotation. The arc-click handler builds a new
 *                  `{...arcData, isPinned, selected}` object for the clicked arc and folds the whole
 *                  chartData — already carrying this key — back into the dispatched segments on
 *                  EVERY arc click, including a bare select. The key is never set before a segment's
 *                  first click, so a bare select adds it where it was previously absent — an
 *                  undefined-vs-missing diff `isEqual` (and this comparator) would otherwise treat
 *                  as a real change, with zero `settings` ever moving.
 */
const DIRTY_IGNORED_SEGMENT_FIELDS = [
  "selected",
  "initialNode",
  "is_modified",
  "_labelDisplay",
  "isPinned",
];

const stripDirtyIgnoredFields = (rest) => {
  const clean = { ...rest };
  for (const key of DIRTY_IGNORED_SEGMENT_FIELDS) delete clean[key];
  return clean;
};

/**
 * areSegmentsStructurallyEqual — deep-equality for two `wheel_segments` arrays that ignores
 * cosmetic differences in each segment's serialized `nodes` STRING and transient editor-only
 * bookkeeping fields (see DIRTY_IGNORED_SEGMENT_FIELDS).
 *
 * Every other field is compared with `isEqual`; the `nodes` field is compared by parsing it first,
 * so a craft.js serialize round-trip (same node tree, different JSON string) is treated as "no
 * change". This is the guard that lets us tell a real user edit apart from the canvas re-serializing
 * itself after an undo/redo re-hydrated it — or, on builder mount, the first canvas serialize
 * flipping the transient `is_modified` flag (the residual false "unsaved changes" QA row 23/19).
 */
export const areSegmentsStructurallyEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const sa = a[i] || {};
    const sb = b[i] || {};
    if (sa === sb) continue;

    const { nodes: nodesA, ...restA } = sa;
    const { nodes: nodesB, ...restB } = sb;
    if (!isEqual(stripDirtyIgnoredFields(restA), stripDirtyIgnoredFields(restB)))
      return false;
    if (!nodesStructurallyEqual(nodesA, nodesB)) return false;
  }
  return true;
};

/**
 * syncCanvasEdit — the undoable entry point for a craft.js canvas edit (block add / remove / move /
 * prop change), used by the SegmentBuilder `editorState` -> Redux sync.
 *
 * Why not just `withHistory(setWheelSegments(...))`? Because the canvas re-serializes itself every
 * time an undo/redo re-hydrates it (SegmentView deserializes the reverted nodes -> craft fires
 * onNodesChange -> the editor state sync runs again). That echo carries the SAME node tree the undo
 * just restored, but as a freshly-serialized JSON STRING. `withHistory`'s string-level `isEqual`
 * saw a "different" value and recorded it as a new edit, which bounced `currentIndex` forward and
 * silently CANCELED the undo (WEB-037: block add/remove undo did nothing for all 13 block types).
 *
 * By comparing STRUCTURALLY first, a round-trip echo is a true no-op and never touches history, so
 * an undo/redo sticks. A genuine structural change (a block really was added/removed/edited) still
 * records exactly one correctly-ordered, de-duplicated step via `withHistory` — which additionally
 * suppresses recording while an undo/redo is settling (WEB-038), so the boundary-crossing echo whose
 * re-serialized tree genuinely differs (craft normalizing a backend-default segment's nodes) can't
 * record a phantom snapshot either.
 */
export const syncCanvasEdit = (nextSegments) => (dispatch, getState) => {
  const prevSegments = getPresentState(getState())?.wheel_segments || [];
  if (areSegmentsStructurallyEqual(prevSegments, nextSegments)) {
    return; // serialize round-trip echo (same tree, different string) — not a user edit
  }
  dispatch(withHistory(setWheelSegments(nextSegments)));
};

/**
 * withHistoryIgnoringSelection — like `withHistory`, but a checkpoint is recorded only when the
 * mutation changes real wheel content, not when it only flips per-segment selection/transient
 * bookkeeping (see `DIRTY_IGNORED_SEGMENT_FIELDS`).
 *
 * Root cause (builder-fresh-unsaved): clicking a segment to select/open it, or closing it, updates
 * ONLY `wheel_segments[i].selected` via `setWheelSegments`. Plain `withHistory` diffs the FULL
 * present object with a naive `isEqual`, which does not know `selected` is UI-only — so a pure
 * selection click looked exactly like a content edit, recorded a checkpoint, bounced `currentIndex`
 * past `lastSavedIndex`, and flipped the autosave chip to "Unsaved changes" even though nothing the
 * user could save had changed.
 *
 * The mutation is still ALWAYS applied (unlike `syncCanvasEdit`, which can skip applying a pure
 * round-trip echo) because `selected` is read back out of this same present state to decide which
 * segment panel is open — only the CHECKPOINT/dirty decision ignores it. Any other field difference
 * (position, settings, nodes, wheel-level fields) is still compared normally and still records a
 * checkpoint exactly like `withHistory`.
 */
export const withHistoryIgnoringSelection = (action) => (dispatch, getState) => {
  if (getState().segmentBuilder?.historySuppressed) {
    dispatch(action);
    return;
  }

  const before = cloneDeep(getPresentState(getState()));
  dispatch(action);
  const after = getPresentState(getState());

  const { wheel_segments: segmentsBefore, ...restBefore } = before || {};
  const { wheel_segments: segmentsAfter, ...restAfter } = after || {};
  const changed =
    !isEqual(restBefore, restAfter) ||
    !areSegmentsStructurallyEqual(segmentsBefore || [], segmentsAfter || []);

  if (changed) {
    dispatch(recordHistorySnapshot(before));
  }
};

export default withHistory;
